/*
  Carnevore Mountain welcome pop-up (sections/cm-raw-popup.liquid). Loaded with defer; no libraries.
  <cm-raw-popup> opens its <dialog> once, after a delay or after the visitor has scrolled (whichever comes first), and
  only when:
  - the visitor hasn't closed it, or signed up, within the last data-days days (localStorage);
  - the page isn't the cart, checkout or account pages, or a handle in data-hidden;
  - nothing else is open (another dialog, or a visitor typing in a field).
  Closing it (the X, Esc, the decline button, a click outside) is remembered for data-days days, and a small tab at the
  bottom left (data-popup-teaser) lets them open it again until they sign up. Signing up (the form's
  .is-sent class, set by cm-raw-signup.js) is remembered for a year, and the copy button copies the code.
  A text step follows the email sign-up when an SMS list is set. Browsers that can't store anything still work: the pop-up then shows once per page view.
*/
if (!customElements.get('cm-raw-popup')) {
  const KEY = 'cm-raw-popup';
  const store = {
    get(name) {
      try {
        return Number(window.localStorage.getItem(`${KEY}-${name}`)) || 0;
      } catch (e) {
        return 0;
      }
    },
    set(name) {
      try {
        window.localStorage.setItem(`${KEY}-${name}`, String(Date.now()));
      } catch (e) {
        /* the pop-up just comes back next visit */
      }
    },
  };
  const DAY = 86400000;

  customElements.define(
    'cm-raw-popup',
    class CmRawPopup extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[data-popup-dialog]');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
        this.days = Number(this.dataset.days) || 7;
        this.design = this.hasAttribute('data-design-mode');
        this.teaser = this.querySelector('[data-popup-teaser]');
        if (this.teaser && !this.teaser.textContent.trim()) this.teaser = null;
        if (this.teaser) this.teaser.addEventListener('click', () => this.open(true));
        this.dialog.addEventListener('close', () => this.closed());
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.dialog.close(); // a click on the backdrop
        });
        this.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', () => this.dialog.close()));
        const copy = this.querySelector('[data-popup-copy]');
        if (copy) copy.addEventListener('click', () => this.copy(copy));
        this.sms = this.querySelector('[data-popup-sms]');
        if (this.sms) this.initSms();
        const form = this.querySelector('form');
        if (form) {
          new MutationObserver(() => {
            if (form.classList.contains('is-sent')) {
              store.set('done');
              this.sent = true;
              if (this.teaser) this.teaser.hidden = true;
            }
          }).observe(form, { attributeFilter: ['class'] });
        }
        if (!this.allowed()) {
          // Closed recently and not signed up: the tab lets them come back to the offer.
          if (this.pageAllowed() && Date.now() - store.get('done') >= 365 * DAY && store.get('closed')) this.showTeaser();
          return;
        }
        const open = () => this.open();
        this.timer = window.setTimeout(open, (Number(this.dataset.delay) || 8) * 1000);
        const percent = Number(this.dataset.scroll) || 0;
        if (percent) {
          this.onScroll = () => {
            const room = document.documentElement.scrollHeight - window.innerHeight;
            if (room > 0 && (window.scrollY / room) * 100 >= percent) open();
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
        }
      }

      disconnectedCallback() {
        this.stop();
      }

      pageAllowed() {
        if (this.design) return true;
        const path = window.location.pathname.replace(/\/+$/, '');
        if (/^\/(cart|checkout|account|password)(\/|$)/.test(path) || /\/(checkouts|orders)\//.test(path)) return false;
        const hidden = (this.dataset.hidden || '').split(',').map((h) => h.trim()).filter(Boolean);
        return !hidden.includes(this.dataset.page);
      }

      showTeaser() {
        if (this.teaser) this.teaser.hidden = false;
      }

      allowed() {
        if (this.design) return true;
        if (!this.pageAllowed()) return false;
        if (Date.now() - store.get('done') < 365 * DAY) return false;
        if (Date.now() - store.get('closed') < this.days * DAY) return false;
        return true;
      }

      stop() {
        window.clearTimeout(this.timer);
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      open(fromTeaser = false) {
        this.stop();
        if (this.dialog.open) return;
        if (fromTeaser === true && this.teaser) this.teaser.hidden = true;
        const active = document.activeElement;
        if (document.querySelector('dialog[open]') || (active && /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName))) {
          // Someone is busy: try again shortly.
          this.timer = window.setTimeout(() => this.open(), 5000);
          return;
        }
        this.opener = active;
        this.dialog.showModal();
        const input = this.querySelector('input[type="email"]');
        if (input) input.focus();
      }

      closed() {
        if (!this.design) store.set('closed');
        if (!this.sent && this.pageAllowed()) this.showTeaser();
        if (this.opener && typeof this.opener.focus === 'function' && document.contains(this.opener)) this.opener.focus();
      }

      // The optional text step: the phone number (with the email, so it joins the same profile) goes to Klaviyo's Client
      // Subscriptions API with SMS marketing consent. The consent wording sits next to the button.
      initSms() {
        const send = this.sms.querySelector('[data-popup-sms-send]');
        const input = this.sms.querySelector('[data-popup-phone]');
        send.addEventListener('click', () => this.sendSms(send, input));
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.sendSms(send, input);
          }
        });
        input.addEventListener('input', () => {
          this.sms.querySelector('[data-popup-sms-error]').hidden = true;
          input.removeAttribute('aria-invalid');
        });
      }

      // US and Canadian numbers: 10 digits, 11 starting with 1, or +1 and 10 digits. Others need a + and the country code.
      phone(value) {
        const digits = value.replace(/[^\d+]/g, '');
        const plain = digits.replace(/\D/g, '');
        if (!digits.startsWith('+') && plain.length === 10) return `+1${plain}`;
        if (!digits.startsWith('+') && plain.length === 11 && plain.startsWith('1')) return `+${plain}`;
        if (digits.startsWith('+') && plain.length >= 8 && plain.length <= 15) return `+${plain}`;
        return null;
      }

      async sendSms(button, input) {
        const { key, list, source } = this.sms.dataset;
        const error = this.sms.querySelector('[data-popup-sms-error]');
        const number = this.phone(input.value);
        if (!number) {
          input.setAttribute('aria-invalid', 'true');
          error.hidden = false;
          error.focus();
          return;
        }
        const email = (this.querySelector('input[type="email"]') || {}).value;
        button.disabled = true;
        button.textContent = button.dataset.sending;
        let response = null;
        try {
          response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(key)}`, {
            method: 'POST',
            headers: { 'content-type': 'application/vnd.api+json', accept: 'application/vnd.api+json', revision: '2024-10-15' },
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: source || undefined,
                  profile: {
                    data: {
                      type: 'profile',
                      attributes: { ...(email ? { email: email.trim() } : {}), phone_number: number, subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } } },
                    },
                  },
                },
                relationships: { list: { data: { type: 'list', id: list } } },
              },
            }),
          });
        } catch (e) {
          response = null;
        }
        button.disabled = false;
        button.textContent = button.dataset.label;
        if (response && response.ok) {
          this.sms.querySelector('[data-popup-sms-fields]').hidden = true;
          error.hidden = true;
          const sent = this.sms.querySelector('[data-popup-sms-sent]');
          sent.hidden = false;
          sent.focus();
        } else {
          input.setAttribute('aria-invalid', 'true');
          error.hidden = false;
          error.focus();
        }
      }

      async copy(button) {
        const code = this.querySelector('[data-popup-code]');
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code.textContent.trim());
          button.textContent = button.dataset.copied;
          window.setTimeout(() => {
            button.textContent = button.dataset.label;
          }, 2500);
        } catch (e) {
          // No clipboard access: select the code so it can be copied by hand.
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }
    }
  );
}
